import {
	faArrowRightFromBracket,
	faChevronRight,
	faHeadset,
	faKey,
	faSpinner,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	biometricIconFor,
	ConfirmDialog,
	getStoredBiometricEmail,
	useBiometricLogin,
	useTranslation,
} from '@supply-mover-2-0/web-ui';
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AccountDashboard } from '../components/dashboard/AccountDashboard';
import { AccountProfileWidget } from '../components/dashboard/widgets/AccountProfileWidget';
import { AccountSnapshotWidget } from '../components/dashboard/widgets/AccountSnapshotWidget';
import { MobilePageHeader } from '../components/layout/MobilePageHeader';
import { PrimaryPaymentMethodCard } from '../components/PrimaryPaymentMethodCard';
import { RecentInvoicesCard } from '../components/RecentInvoicesCard';
import { RecentOrdersCard } from '../components/RecentOrdersCard';
import { RecentStatementsCard } from '../components/RecentStatementsCard';
import { RecentTransactionsCard } from '../components/RecentTransactionsCard';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { triggerLightImpact } from '../lib/haptics';
import { maskPhoneNumber } from '../lib/maskPhone';
import { getStorefrontAccountDashboard } from '../services/portalDashboardService';
import { useLoginModalStore } from '../stores/loginModalStore';
import { usePortalAuthStore } from '../stores/portalAuthStore';

/**
 * Account page. Now config-driven: if the portal site has an account-page
 * design (authored in CRM Settings → Ecommerce → Account Page Designs), it
 * renders that widget layout via `AccountDashboard`. Otherwise it falls
 * back to the hardcoded default layout (profile, account snapshot, primary
 * payment method, 2×2 recent orders/invoices/transactions/statements) — so
 * no portal is forced to adopt a design.
 *
 * The page header, the mobile Change-Password menu, and Sign Out are fixed
 * chrome shown in BOTH paths, so logout + password change are always
 * reachable regardless of the configured design.
 */
export function AccountPage(): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { token, logout, isAuthenticated, profile, activeClient } =
		usePortalAuth();
	const openLoginModal = useLoginModalStore((s) => s.open);

	// Biometric quick sign-in enroll/unenroll (PD-14765). Only rendered in
	// native shells on devices with an enrolled biometric — web builds and
	// biometric-less devices never see the row.
	const biometric = useBiometricLogin();
	const showBiometricRow = biometric.isNative && biometric.isAvailable;
	const [isBiometricBusy, setIsBiometricBusy] = useState(false);
	const [enrolledEmail, setEnrolledEmail] = useState<string | null>(null);
	const [showEnrollConfirm, setShowEnrollConfirm] = useState(false);

	// Display label for the enrollment, derived from whatever the user
	// signed in with — no re-entry of any information (PD-14772). Email
	// when present, else the masked phone (SMS sign-in users often have no
	// email on file), else the profile or account name. The label is
	// display-only; the stored credential is always the refresh token.
	const enrollLabel = ((): string => {
		if (profile == null) {
			return '';
		}
		if (profile.email != null && profile.email !== '') {
			return profile.email;
		}
		if (profile.phoneNumber != null && profile.phoneNumber !== '') {
			return maskPhoneNumber(profile.phoneNumber);
		}
		const name = `${profile.firstName ?? ''} ${profile.lastName ?? ''}`.trim();
		if (name !== '') {
			return name;
		}
		return activeClient?.name ?? '';
	})();

	// Label the row with the account the stored session belongs to. Un-gated
	// read — never triggers a biometric prompt.
	useEffect(() => {
		if (!biometric.hasStoredSession) {
			setEnrolledEmail(null);
			return;
		}
		void getStoredBiometricEmail().then(setEnrolledEmail);
	}, [biometric.hasStoredSession]);

	// Ref-based re-entrancy guard alongside the busy state: the state
	// lands a render later, so rapid repeat taps could re-enter before the
	// button disables; the ref closes that window synchronously.
	const biometricToggleInFlightRef = useRef(false);
	const handleBiometricToggle = useCallback(async () => {
		if (isBiometricBusy || biometricToggleInFlightRef.current) {
			return;
		}
		if (!biometric.hasStoredSession) {
			// Enrollment is confirmed first; the actual enroll runs from the
			// dialog's confirm action.
			setShowEnrollConfirm(true);
			return;
		}
		biometricToggleInFlightRef.current = true;
		setIsBiometricBusy(true);
		try {
			// Turning off never needs a biometric prompt — it only deletes
			// the stored session.
			await biometric.disable();
			triggerLightImpact();
		} finally {
			biometricToggleInFlightRef.current = false;
			setIsBiometricBusy(false);
		}
	}, [biometric, isBiometricBusy]);

	const handleEnrollConfirmed = useCallback(async () => {
		setShowEnrollConfirm(false);
		if (biometricToggleInFlightRef.current) {
			return;
		}
		biometricToggleInFlightRef.current = true;
		setIsBiometricBusy(true);
		try {
			// Enrolling from settings uses the LIVE session: the current
			// refresh token (read at tap time — it rotates) plus the derived
			// label. enable() shows the one consent prompt and stores both.
			const refreshToken = usePortalAuthStore.getState().refreshToken;
			if (enrollLabel === '' || refreshToken == null) {
				return;
			}
			const enabled = await biometric.enable({
				email: enrollLabel,
				refreshToken,
			});
			if (enabled) {
				triggerLightImpact();
			}
		} finally {
			biometricToggleInFlightRef.current = false;
			setIsBiometricBusy(false);
		}
	}, [biometric, enrollLabel]);

	// Signed-out visitors who reach this authenticated page by direct URL /
	// deep link (or after a session expiry) get the login modal over an empty
	// page — matching the sibling account pages (Orders/Invoices/…) instead
	// of half-signed-in chrome (PD-14185).
	useEffect(() => {
		if (!isAuthenticated) {
			openLoginModal();
		}
	}, [isAuthenticated, openLoginModal]);

	// The cart deep-links to /account?addPayment=true; the add form now lives
	// on the payment methods page, so forward the deep link there.
	const autoOpenAddPayment = searchParams.get('addPayment') === 'true';
	useEffect(() => {
		if (autoOpenAddPayment) {
			void navigate('/account/payment-methods?addPayment=true', {
				replace: true,
			});
		}
	}, [autoOpenAddPayment, navigate]);

	// Probe for a configured account-page design. Shares the query key with
	// AccountDashboard so the design is fetched once. A design with at least
	// one widget switches the page to the config-driven layout; otherwise the
	// hardcoded fallback below renders.
	const accountDesignProbe = useQuery({
		queryKey: ['portal', 'account-page'],
		queryFn: () => getStorefrontAccountDashboard(token),
		enabled: isAuthenticated && token != null,
		staleTime: 60_000,
	});
	const hasAccountDesign =
		(accountDesignProbe.data?.data?.widgets?.length ?? 0) > 0;

	if (!isAuthenticated) {
		return <div />;
	}

	const menuItems = [
		{ label: 'Change Password', icon: faKey, path: '/account/password' },
		// PD-13773 — the customer's submitted issues (desktop reaches this
		// via the sidebar's Support item).
		{ label: 'Support Requests', icon: faHeadset, path: '/support' },
	];

	return (
		// max-w-4xl matches Invoices/Orders (PD-14076)
		<div className="px-4 py-4 max-w-4xl mx-auto">
			<MobilePageHeader title="Account" backPath="/dashboard" />
			<h1 className="hidden md:block text-lg md:text-xl font-bold text-default mb-4">
				{t('Account')}
			</h1>

			{/* One vertical rhythm for the whole page: the widget area (or its
			    fallback), the mobile menu, and Sign Out are all direct children
			    of this `space-y-6` container, so every section — including the
			    gap above Sign Out — is an equal 24px apart. AccountDashboard
			    keeps its own internal `space-y-6` for its rows, matching. */}
			<div className="space-y-6">
				{accountDesignProbe.isLoading ? (
					<div className="animate-pulse space-y-4">
						<div className="h-24 bg-brand-input rounded-xl" />
						<div className="h-40 bg-brand-input rounded-xl" />
					</div>
				) : hasAccountDesign ? (
					<AccountDashboard />
				) : (
					<>
						{/* Hardcoded default layout — used until an admin configures
						    an account-page design for this portal. Reuses the same
						    widget components the designer drops in. */}
						<AccountProfileWidget />
						<AccountSnapshotWidget />
						<PrimaryPaymentMethodCard />
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<RecentOrdersCard />
							<RecentInvoicesCard />
							<RecentTransactionsCard />
							<RecentStatementsCard />
						</div>
					</>
				)}

				{/* Chrome (both paths): mobile-only Change Password menu — desktop
				    surfaces this in the profile widget's header. */}
				<div className="md:hidden bg-brand-surface border border-brand-border rounded-xl overflow-hidden divide-y divide-subtle">
					{menuItems.map((item) => (
						<button
							key={item.path}
							type="button"
							onClick={() => {
								void navigate(item.path);
							}}
							className="w-full flex items-center gap-3 px-4 py-3.5 text-sm text-strong hover:bg-brand-input min-h-touch"
						>
							<FontAwesomeIcon
								icon={item.icon}
								className="w-5 text-brand-muted"
							/>
							<span className="flex-1 text-left">{t(item.label)}</span>
							<FontAwesomeIcon
								icon={faChevronRight}
								className="text-xs text-disabled"
							/>
						</button>
					))}
				</div>

				{/* Chrome (both paths): biometric quick sign-in enroll/unenroll
				    (PD-14765) — native shells with a usable biometric only,
				    placed directly above Sign Out. */}
				{showBiometricRow && (
					<div className="bg-brand-surface border border-brand-border rounded-xl overflow-hidden">
						<button
							type="button"
							onClick={() => {
								void handleBiometricToggle();
							}}
							disabled={isBiometricBusy}
							className="w-full flex items-center gap-3 px-4 py-3.5 text-sm text-strong hover:bg-brand-input disabled:opacity-60 min-h-touch"
						>
							<FontAwesomeIcon
								icon={biometricIconFor(biometric.biometryType)}
								className="w-5 text-brand-muted"
							/>
							<span className="flex-1 text-left">
								{biometric.hasStoredSession
									? t(`Turn Off ${biometric.biometryName} Sign-In`)
									: t(`Enable ${biometric.biometryName} Sign-In`)}
							</span>
							{isBiometricBusy && (
								<FontAwesomeIcon
									icon={faSpinner}
									className="animate-spin text-brand-muted"
								/>
							)}
						</button>
						{biometric.hasStoredSession &&
							enrolledEmail != null &&
							enrolledEmail !== '' && (
								<p className="px-4 pb-3 text-xs text-brand-muted">
									{t('Enabled for')} {t(enrolledEmail)}
								</p>
							)}
					</div>
				)}

				{showBiometricRow && (
					<ConfirmDialog
						isOpen={showEnrollConfirm}
						variant="info"
						title={t(`Enable ${biometric.biometryName} Sign-In`)}
						message={t(
							`Quick sign-in with ${biometric.biometryName} will be enabled on this device for ${enrollLabel}.`,
						)}
						confirmText={t('Enable')}
						cancelText={t('Cancel')}
						onConfirm={() => {
							void handleEnrollConfirmed();
						}}
						onCancel={() => {
							setShowEnrollConfirm(false);
						}}
					/>
				)}

				{/* Chrome (both paths): Sign out always present, regardless of the
				    configured design. */}
				<button
					type="button"
					onClick={logout}
					className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-danger bg-brand-surface border border-brand-border rounded-xl hover:bg-danger-tint transition-colors min-h-touch"
				>
					<FontAwesomeIcon icon={faArrowRightFromBracket} />
					<span>{t('Sign Out')}</span>
				</button>
			</div>
		</div>
	);
}
